import SupplierProductDTO from "@/dto/dropShipping/SupplierProductDTO";
import TaskStatus from "@/enum/TaskStatus";
import { get, post } from "@/services/http/platformServer";
import { WrapperList } from "@/utils/utilityTypes";

export interface GetSupplierProductParams {
    supplierShopId: number;
    retailerShopId: number;
    title?: string;
    externalId?: string;
    productOriginalId?: string;
    pageNo: number;
    pageSize: number;
    noSimpleTitle?: boolean;
    noWeight?: boolean;
}
export function getSupplierProduct(params: GetSupplierProductParams): Promise<WrapperList<SupplierProductDTO>> {
    return post(params.supplierShopId, "/drop_shipping/supplier_product/query", params);
}

interface AsyncOperationTask {
    /**
     * 错误信息
     */
    error?: string;
    /**
     * 任务ID
     */
    taskId: string;
    /**
     * 任务结果
     */
    result?: null;
    /**
     * 店铺ID
     */
    shopId: number;
    /**
     * 任务状态
     */
    taskStatus: TaskStatus;
}

export function submitSyncSupplierProductTask(supplierShopId: number, retailerShopId: number) {
    return post<AsyncOperationTask>(supplierShopId, "/drop_shipping/supplier_product/sync", {
        supplierShopId,
        retailerShopId,
    });
}

export function getSyncSupplierProductTaskResult(shopId: number, taskId: string) {
    return get<AsyncOperationTask>(shopId, "/drop_shipping/supplier_product/get_sync_result", {
        taskId,
    });
}

interface UpdateSupplierProductSimpleTitleParams {
    shopId: number;
    productId: number;
    simpleTitle: string;
}

export function updateSupplierProductSimpleTitle(
    params: UpdateSupplierProductSimpleTitleParams,
): Promise<{ successful: boolean }> {
    return post(params.shopId, "/drop_shipping/supplier_product/update_product_simple_title", params);
}

interface UpdateSupplierSkuSimpleTitleParams {
    shopId: number;
    productId: number;
    skuId: number;
    simpleTitle: string;
}

export function updateSupplierSkuSimpleTitle(params: UpdateSupplierSkuSimpleTitleParams) {
    return post(params.shopId, "/drop_shipping/supplier_product/update_sku_simple_title", params);
}

interface BatchUpdateSupplierProductWeightParams {
    shopId: number;
    productIds: number[];
    weight: number;
}

export function batchUpdateSupplierProductWeight(params: BatchUpdateSupplierProductWeightParams) {
    return post(params.shopId, "/drop_shipping/supplier_product/batch_update_product_weight", params);
}

interface UpdateSupplierSkuWeightParams {
    shopId: number;
    productId: number;
    skuId: number;
    weight: number;
}

export function updateSupplierSkuWeight(params: UpdateSupplierSkuWeightParams) {
    return post(params.shopId, "/drop_shipping/supplier_product/update_sku_weight", params);
}

interface UpdateSupplierProductStockNoParams {
    shopId: number;
    productId: number;
    stockNo: string;
}

export function updateSupplierProductStockNo(
    params: UpdateSupplierProductStockNoParams,
): Promise<{ successful: boolean }> {
    return post(params.shopId, "/drop_shipping/supplier_product/update_product_stock_no", params);
}

interface UpdateSupplierSkuStockNoParams {
    shopId: number;
    productId: number;
    skuId: number;
    stockNo: string;
}

export function updateSupplierSkuStockNo(params: UpdateSupplierSkuStockNoParams) {
    return post(params.shopId, "/drop_shipping/supplier_product/update_sku_stock_no", params);
}

export interface SubmitSupplierProductExportTaskParams {
    /** 需要导出的选中的供货商商品参数，勾选商品时传递 */
    supplierProductSelectParams?: {
        supplierShopId: number;
        retailerShopId: number;
        productIds: number[];
    };
    /** 需要导出的供货商商品列表查询参数，未勾选商品时传递，最多导出前 5000 条 */
    supplierProductQueryParam?: GetSupplierProductParams;
}

export interface SupplierProductExportTask {
    id: string;
    supplierShopId: number;
    retailerShopId: number;
    status: TaskStatus;
    downloadUrl?: string;
}

/** 发起导出供货商商品信息任务 */
export function submitSupplierProductExportTask(
    supplierShopId: number,
    params: SubmitSupplierProductExportTaskParams,
) {
    return post<SupplierProductExportTask>(supplierShopId, "/supplier_product_export_task/export", params);
}

/** 查询导出供货商商品信息任务结果 */
export function querySupplierProductExportTask(supplierShopId: number, taskId: string) {
    return post<SupplierProductExportTask>(supplierShopId, `/supplier_product_export_task/export/${taskId}`, { taskId });
}

export interface AsyncImportSupplierProductSettingParams {
    supplierShopId: number;
    retailerShopId: number;
    file: File;
}

export interface SupplierProductSettingImportResultErrorRow {
    /** 错误行号 */
    rowNum?: number;
    /** 错误信息 */
    message?: string;
}

export interface SupplierProductSettingImportResult {
    /** 成功导入的数量 */
    successCount?: number;
    /** 导入失败的信息 */
    errors?: SupplierProductSettingImportResultErrorRow[];
}

export interface SupplierProductSettingImportTask {
    id: string;
    supplierShopId: number;
    retailerShopId: number;
    taskStatus: TaskStatus;
    result: SupplierProductSettingImportResult;
    error?: string;
}

/** 导入供货商商品设置 */
export function asyncImportSupplierProductSetting(params: AsyncImportSupplierProductSettingParams) {
    const { supplierShopId, retailerShopId, file } = params;

    const formData = new FormData();
    formData.append("file", file);

    return post<SupplierProductSettingImportTask>(
        supplierShopId,
        "/drop_shipping/supplier_product/async_import_setting",
        formData,
        {
            params: {
                supplierShopId,
                retailerShopId,
            },
        },
    );
}

export interface QnAsyncImportSupplierProductSettingParams {
    supplierShopId: number;
    retailerShopId: number;
    excelFileUrl: string;
    excelFileOriginalName: string;
    excelFileSize: number;
}

export function qnAsyncImportSupplierProductSetting(params: QnAsyncImportSupplierProductSettingParams) {
    return post<SupplierProductSettingImportTask>(
        params.supplierShopId,
        "/drop_shipping/supplier_product/qianniu/async_import_setting",
        params,
    );
}

export function getSupplierProductSettingImportResult(supplierShopId: number, taskId: string) {
    return get<SupplierProductSettingImportTask>(supplierShopId, "/drop_shipping/supplier_product/setting_import_result", {
        taskId,
    });
}
